/**
 * LICITARS 3.0 - Premium Sidebar with Role-Based Navigation
 * Exact reproduction of licitars-3-0 Layout.jsx sidebar structure
 */

import React, { useMemo } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  FolderPlus,
  Users,
  DollarSign,
  Bot,
  GitBranch,
  Scale,
  Settings,
  BookOpen,
  Archive,
  BarChart,
  ChevronRight,
  LogOut,
  Building2,
  MessageSquare,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import LicitarsLogo from "../LicitarsLogo";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";

interface AppSidebarV3Props {
  userRole?: "master" | "admin" | "gestor" | "operador";
}

const AppSidebarV3: React.FC<AppSidebarV3Props> = ({ userRole = "operador" }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  const handleLogout = () => {
    logout();
  };

  // Master Navigation (Evoluta - Superuser)
  const masterNavigation = useMemo(
    () => [
      // { path: "/master/dashboard", icon: LayoutDashboard, label: "Dashboard Master" },
      // { path: "/master/clients", icon: Building2, label: "Gestão de Clientes" },
      // { path: "/master/pricing", icon: DollarSign, label: "Precificação" },
      // { path: "/master/agents", icon: Bot, label: "Agentes de IA" },
      // { path: "/master/mapping", icon: GitBranch, label: "Mapeamentos" },
      // { path: "/master/parameters", icon: Scale, label: "Parâmetros Legais" },
      // { path: "/master/docs", icon: BookOpen, label: "Documentação Técnica" },
    ],
    []
  );

  // Client Navigation (Admin/Gestor/Operador)
  const clientNavigation = useMemo(
    () => [
      { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard", adminOnly: false },
      { path: "/processes", icon: FolderPlus, label: "Processos", adminOnly: false },
      { path: "/documents", icon: FileText, label: "Gerar Documento", adminOnly: false },
      { path: "/chat8", icon: MessageSquare, label: "LicitarsAI", adminOnly: false },
      // { path: "/knowledge", icon: BookOpen, label: "Base de Conhecimento", adminOnly: true },
      // { path: "/blocks", icon: Settings, label: "Blocos Padrão", adminOnly: true },
      // { path: "/archive", icon: Archive, label: "Arquivo" },
      // { path: "/reports", icon: BarChart, label: "Relatórios", adminOnly: true },
      // { path: "/admin", icon: Users, label: "Administração", adminOnly: true },
    ],
    []
  );

  // Select and filter navigation based on role
  const navigation = useMemo(() => {
    if (userRole === "master") {
      return masterNavigation;
    }
    
    // Filter client navigation by role
    const isAdmin = userRole === "admin";
    const isGestor = userRole === "gestor";
    
    return clientNavigation.filter(
      (item) => !item.adminOnly || isAdmin || (item.label === "Relatórios" && isGestor)
    );
  }, [userRole, masterNavigation, clientNavigation]);

  return (
    <aside
      className="flex flex-col h-full border-r border-navy-500 w-64 bg-navy-700"
    >
      {/* Logo Header */}
      <div className="p-6 flex flex-col gap-2 border-b border-navy-500 bg-navy-700">
        <div className="flex flex-col items-center gap-3">
          <div className="w-full flex justify-center p-3 rounded-xl bg-navy-700">
            <LicitarsLogo size="medium" />
          </div>
          
          {/* Master Badge */}
          {userRole === "master" && (
            <div className="px-3 py-1.5 rounded-full gradient-primary border border-primary shadow-lg">
              <span className="text-xs font-semibold text-white uppercase tracking-wide">
                Painel Master
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Content: Navigation + Credits */}
      <div className="flex-1 overflow-y-auto p-3 bg-navy-700">
        {/* Navigation Section */}
        <div className="mb-4">
          <h3 className="text-xs font-medium text-steel-500 uppercase tracking-wider px-3 py-3">
            Navegação
          </h3>
          <nav>
            <ul className="space-y-0">
              {navigation.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);

                return (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      className={cn(
                        "flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 mb-1 font-medium",
                        active
                          ? "gradient-primary text-white shadow-lg shadow-accent"
                          : "text-muted-foreground hover:bg-navy-600 hover:text-foreground"
                      )}
                    >
                      <Icon className="w-5 h-5" />
                      <span>{item.label}</span>
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
      </div>

      {/* Footer: User Profile */}
      <div className="border-t border-navy-500 p-4 bg-navy-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-11 h-11 gradient-primary-br rounded-full flex items-center justify-center shadow-lg shadow-accent">
              <span className="text-white font-bold text-base">
                {user?.username?.charAt(0).toUpperCase() || "U"}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-foreground text-sm truncate">
                {user?.username || "Usuário"}
              </p>
              <p className="text-xs text-steel-500 truncate font-medium uppercase tracking-wide">
                {userRole === "master" 
                  ? "Master Evoluta" 
                  : userRole === "admin" 
                    ? "Admin" 
                    : userRole === "gestor" 
                      ? "Gestor" 
                      : "Operador"}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleLogout}
            className="text-muted-foreground hover:text-foreground hover:bg-navy-600"
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </aside>
  );
};

export default AppSidebarV3;